import { useEffect, useRef } from 'react';
import type { FormEvent } from 'react';
import { Avatar, MessageItemLayout, MessageList, Stack, ThreadHeader } from '@room/shared-ui';
import type { ChatMessage, ChatSummary, ConnectionStatus } from '../types';
import { MessageComposer } from './MessageComposer';

export type ChatThreadPanelProps = {
  activeChat: ChatSummary | null;
  memberLabel: string | null;
  messages: ChatMessage[];
  typingUsers: string[];
  currentUserId: string | null;
  isLoadingMessages: boolean;
  error: string | null;
  messageDraft: string;
  onMessageChange: (value: string) => void;
  onSendMessage: (event: FormEvent<HTMLFormElement>) => void;
  onBackToChats: () => void;
  connectionStatus: ConnectionStatus;
  getChatDisplayName: (chat: ChatSummary) => string;
  showMobileMessages: boolean;
};

// Helper to check if messages should be grouped (same sender, within 5 minutes)
function shouldGroupWithPrevious(current: ChatMessage, previous: ChatMessage | undefined): boolean {
  if (!previous) return false;
  if (current.senderId !== previous.senderId) return false;

  const timeDiff = new Date(current.timestamp).getTime() - new Date(previous.timestamp).getTime();
  const fiveMinutes = 5 * 60 * 1000;

  return timeDiff < fiveMinutes;
}

export function ChatThreadPanel({
  activeChat,
  memberLabel,
  messages,
  typingUsers,
  currentUserId,
  isLoadingMessages,
  error,
  messageDraft,
  onMessageChange,
  onSendMessage,
  onBackToChats,
  connectionStatus,
  getChatDisplayName,
  showMobileMessages,
}: ChatThreadPanelProps) {
  const messageListRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change or chat opens
  useEffect(() => {
    if (messageListRef.current && messages.length > 0) {
      const scrollElement = messageListRef.current;
      scrollElement.scrollTop = scrollElement.scrollHeight;
    }
  }, [messages.length, activeChat?.id]);

  const headerContent = activeChat ? (
    <ThreadHeader
      backButton={(
        <button
          type="button"
          className="chat-thread-back"
          onClick={onBackToChats}
          aria-label="Back to chats"
        >
          ←
        </button>
      )}
      leading={(
        <Avatar
          fallback={getChatDisplayName(activeChat)[0]?.toUpperCase() ?? 'C'}
          size="sm"
        />
      )}
      title={getChatDisplayName(activeChat)}
      subtitle={memberLabel}
      status={(
        <span className={`connection-status status-${connectionStatus}`} aria-label="Connection status">
          {connectionStatus === 'connected' && '●'}
          {connectionStatus === 'connecting' && '◌'}
          {connectionStatus === 'reconnecting' && '◔'}
          {connectionStatus === 'disconnected' && '○'}
        </span>
      )}
    />
  ) : null;

  const footerContent = activeChat ? (
    <MessageComposer
      messageDraft={messageDraft}
      onMessageChange={onMessageChange}
      onSendMessage={onSendMessage}
    />
  ) : null;

  return (
    <Stack
      header={headerContent}
      footer={footerContent}
      scrollable={false}
    >
      <MessageList ref={messageListRef} className="chat-message-list">
        {error && (
          <div className="alert alert-error error-message">
            {error}
          </div>
        )}
        {isLoadingMessages && <p className="loading-text">Loading messages…</p>}

        {!activeChat && !isLoadingMessages && (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <p>Select a chat to start messaging.</p>
          </div>
        )}
        {activeChat && !isLoadingMessages && messages.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">✉️</div>
            <p>No messages yet.</p>
          </div>
        )}
        {activeChat && messages.map((message, index) => {
          const isSent = message.senderId === currentUserId;
          const previousMessage = index > 0 ? messages[index - 1] : undefined;
          const shouldGroup = shouldGroupWithPrevious(message, previousMessage);
          const showAvatar = !isSent && !shouldGroup;
          const showTimestamp = !shouldGroup;

          return (
            <MessageItemLayout
              key={message.id}
              align={isSent ? 'end' : 'start'}
              leading={
                showAvatar ? (
                  <Avatar
                    fallback={message.senderName?.[0]?.toUpperCase() ?? 'U'}
                    size="sm"
                  />
                ) : !isSent ? (
                  <div style={{ width: '28px' }} /> // Placeholder matches avatar size (28px)
                ) : undefined
              }
              content={<div className="message-bubble">{message.content}</div>}
              meta={
                showTimestamp ? (
                  <span className="message-time">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </span>
                ) : null
              }
            />
          );
        })}
        {typingUsers.length > 0 && (
          <p className="typing-indicator">
            {typingUsers.length} user(s) typing…
          </p>
        )}
      </MessageList>
    </Stack>
  );
}
